import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash/get'
import { Controller } from 'react-hook-form'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import FormControl from '@material-ui/core/FormControl'
import FormHelperText from '@material-ui/core/FormHelperText'
import FormLabel from '@material-ui/core/FormLabel'
import TextField from '@material-ui/core/TextField'

const EMPTY_PARTS = { month: '', day: '', year: '' }
const MAX_LENGTH = { month: 2, day: 2, year: 4 }
const SEPARATOR = '\\s*[/.\\-\\s]\\s*'
const PASTED_US_DATE = new RegExp(
  `^\\s*(\\d{1,2})${SEPARATOR}(\\d{1,2})${SEPARATOR}(\\d{4})\\s*$`,
)
const PASTED_ISO_DATE = /^\s*(\d{4})-(\d{1,2})-(\d{1,2})\s*$/
const LOOKS_LIKE_DATE = /\d\s*[/.-]\s*\d/
const ISO_DATE_STRING = /^(\d{4})-(\d{2})-(\d{2})$/
const MONTH_NAMES = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
]

const useStyles = makeStyles(theme => ({
  legend: {
    marginBottom: theme.spacing(1.5),
  },
  fields: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1.5fr',
    gap: theme.spacing(1.5),
    maxWidth: 320,
  },
}))

const pad = value => String(value).padStart(2, '0')

const isValidDate = date => date instanceof Date && !isNaN(date)

const partsFromValue = value => {
  if (isValidDate(value)) {
    return {
      month: pad(value.getMonth() + 1),
      day: pad(value.getDate()),
      year: String(value.getFullYear()),
    }
  }
  const isoMatch = typeof value === 'string' && value.match(ISO_DATE_STRING)
  if (!isoMatch) return EMPTY_PARTS
  const parts = { month: isoMatch[2], day: isoMatch[3], year: isoMatch[1] }
  return isValidDate(dateFromParts(parts)) ? parts : EMPTY_PARTS
}

const valueKey = value => {
  if (value === null || value === undefined || value === '') return ''
  const { month, day, year } = partsFromValue(value)
  return year ? `${year}-${month}-${day}` : 'invalid'
}

const partsFromPaste = text => {
  const us = text.match(PASTED_US_DATE)
  if (us) return { month: pad(us[1]), day: pad(us[2]), year: us[3] }
  const iso = text.match(PASTED_ISO_DATE)
  if (iso) return { month: pad(iso[2]), day: pad(iso[3]), year: iso[1] }
  return null
}

/**
 * Returns `null` when every part is blank, an Invalid Date when the parts do
 * not form a real calendar date, and a local-midnight Date otherwise, so
 * `yup.date().required()` reports "required" vs. "invalid" correctly.
 */
export const dateFromParts = parts => {
  const month = String(parts?.month ?? '')
  const day = String(parts?.day ?? '')
  const year = String(parts?.year ?? '')
  if (!month && !day && !year) return null

  const m = Number(month)
  const d = Number(day)
  const y = Number(year)
  const isWellFormed =
    /^\d{1,2}$/.test(month) && /^\d{1,2}$/.test(day) && /^\d{4}$/.test(year)
  if (!isWellFormed || !m || !d) return new Date(NaN)

  const date = new Date(y, m - 1, d)
  const isRealDate =
    date.getFullYear() === y &&
    date.getMonth() === m - 1 &&
    date.getDate() === d

  return isRealDate ? date : new Date(NaN)
}

const BirthDateField = ({
  id,
  name,
  label = 'Date of Birth',
  value,
  onChange,
  onBlur,
  inputRef,
  error,
  helperText,
  required,
  disabled,
  className,
}) => {
  const classes = useStyles()
  const baseId = id || name
  const helperId = `${baseId}-helper-text`
  const [parts, setParts] = useState(() => partsFromValue(value))
  // Autofill fires one change per input in the same tick, so read the latest
  // parts from a ref instead of the render-time state.
  const partsRef = useRef(parts)
  const lastEmittedRef = useRef(value)

  useEffect(() => {
    const last = lastEmittedRef.current
    const key = valueKey(value)
    const isSameDate = key !== 'invalid' && key === valueKey(last)
    lastEmittedRef.current = value
    if (value === last || isSameDate) return
    partsRef.current = partsFromValue(value)
    setParts(partsRef.current)
  }, [value])

  const emit = nextParts => {
    const date = dateFromParts(nextParts)
    partsRef.current = nextParts
    lastEmittedRef.current = date
    setParts(nextParts)
    onChange(date)
  }

  const handleChange = field => event => {
    const { value: text } = event.target
    const monthIndex =
      field === 'month' && /[a-z]/i.test(text)
        ? MONTH_NAMES.indexOf(
          text
            .trim()
            .slice(0, 3)
            .toLowerCase(),
        )
        : -1
    const digits =
      monthIndex >= 0
        ? pad(monthIndex + 1)
        : text.replace(/\D/g, '').slice(0, MAX_LENGTH[field])
    emit({ ...partsRef.current, [field]: digits })
  }

  const handlePaste = event => {
    const text = event.clipboardData?.getData('text') ?? ''
    const pasted = partsFromPaste(text)
    if (pasted) {
      event.preventDefault()
      emit(pasted)
    } else if (LOOKS_LIKE_DATE.test(text)) {
      event.preventDefault()
    }
  }

  const handleBlur = event => {
    if (event.currentTarget.contains(event.relatedTarget)) return
    if (onBlur) onBlur(event)
  }

  const renderPart = (field, partLabel, placeholder, autoComplete) => (
    <TextField
      id={`${baseId}-${field}`}
      // The month input carries the bare field name so name-based lookups
      // (for example scrollToError) land on the group.
      name={field === 'month' ? name : `${name}.${field}`}
      inputRef={field === 'month' ? inputRef : undefined}
      label={partLabel}
      placeholder={placeholder}
      value={parts[field]}
      onChange={handleChange(field)}
      onPaste={handlePaste}
      error={error}
      disabled={disabled}
      autoComplete={autoComplete}
      InputLabelProps={{ shrink: true }}
      inputProps={{
        inputMode: 'numeric',
        pattern: '[0-9]*',
        // Autofill truncates to maxlength, which would cut a month name to
        // two letters; handleChange caps the digits instead.
        maxLength: field === 'month' ? undefined : MAX_LENGTH[field],
        'aria-required': required || undefined,
        'aria-describedby': helperText ? helperId : undefined,
      }}
    />
  )

  return (
    <FormControl
      component="fieldset"
      error={error}
      required={required}
      disabled={disabled}
      className={className}
      onBlur={handleBlur}
    >
      <FormLabel component="legend" className={classes.legend}>
        {label}
      </FormLabel>
      <Box className={classes.fields}>
        {renderPart('month', 'Month', 'MM', 'bday-month')}
        {renderPart('day', 'Day', 'DD', 'bday-day')}
        {renderPart('year', 'Year', 'YYYY', 'bday-year')}
      </Box>
      {helperText && (
        <FormHelperText id={helperId}>{helperText}</FormHelperText>
      )}
    </FormControl>
  )
}

BirthDateField.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.node,
  value: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  error: PropTypes.bool,
  helperText: PropTypes.node,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
}

export const CtrlBirthDate = ({
  control,
  name,
  errors,
  controllerProps,
  ...other
}) => {
  const fieldError = get(errors, name)

  return (
    <Controller
      defaultValue={null}
      {...controllerProps}
      name={name}
      control={control}
      render={({ onChange, onBlur, value, ref }) => (
        <BirthDateField
          {...other}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          inputRef={ref}
          error={Boolean(fieldError)}
          helperText={get(fieldError, 'message')}
        />
      )}
    />
  )
}

CtrlBirthDate.propTypes = {
  control: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  errors: PropTypes.object,
  controllerProps: PropTypes.object,
}

export default BirthDateField
